import { useState } from 'react'
import { Account, CreateAccountRequest, UpdateAccountRequest } from '../../../../../shared/types'
import { useFormValidation, commonRules } from '../../../shared/hooks'
import { ErrorDisplay, ValidationMessage } from '../../../shared/components'
import { ApiException } from '../../../shared/hooks/useApi'

interface AccountFormProps {
  account?: Account
  onSubmit: (data: CreateAccountRequest | UpdateAccountRequest) => void
  onCancel: () => void
  isLoading?: boolean
  error?: string | Error | ApiException | null
}

export default function AccountForm({
  account,
  onSubmit,
  onCancel,
  isLoading = false,
  error
}: AccountFormProps) {
  const [formData, setFormData] = useState({
    name: account?.name || '',
    type: account?.type || 'asset' as 'asset' | 'liability'
  })

  const validation = useFormValidation({
    name: {
      ...commonRules.required('Account name is required'),
      ...commonRules.minLength(2, 'Account name must be at least 2 characters'),
      ...commonRules.maxLength(100, 'Account name must be 100 characters or less')
    },
    type: commonRules.accountType('Account type must be either "asset" or "liability"')
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validation.validateForm(formData)) {
      return
    }

    const submitData = {
      name: formData.name.trim(),
      type: formData.type
    }

    onSubmit(submitData)
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear validation error when user starts typing
    if (validation.hasError(field)) {
      validation.clearError(field)
    }
  }

  return (
    <div className="account-form-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="account-form" style={{
        backgroundColor: 'var(--container-bg)',
        padding: '2rem',
        borderRadius: '8px',
        border: '1px solid var(--border-color)',
        minWidth: '400px',
        maxWidth: '500px'
      }}>
        <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-light)' }}>
          {account ? 'Edit Account' : 'Create New Account'}
        </h2>

        <ErrorDisplay error={error || null} title="Form Error" />

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="name" style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: 'var(--text-light)',
              fontWeight: '500'
            }}>
              Account Name
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter account name"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--page-bg)',
                border: `1px solid ${validation.hasError('name') ? 'var(--accent-red)' : 'var(--border-color)'}`,
                borderRadius: '4px',
                color: 'var(--text-light)',
                fontSize: '1rem'
              }}
            />
            <ValidationMessage message={validation.getError('name')} />
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label htmlFor="type" style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: 'var(--text-light)',
              fontWeight: '500'
            }}>
              Account Type
            </label>
            <select
              id="type"
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--page-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                color: 'var(--text-light)',
                fontSize: '1rem'
              }}
            >
              <option value="asset">Asset</option>
              <option value="liability">Liability</option>
            </select>
          </div>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'transparent',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                color: 'var(--text-light)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '1rem'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--primary-blue)',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? 'Saving...' : (account ? 'Update Account' : 'Create Account')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}